import { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion, useInView } from 'framer-motion'
import { Phone, Mail, MapPin, Clock, Send, Building2, Users, Briefcase, Globe, MessageSquare, Sparkles, CheckCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

const departments = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'government', label: 'Government Projects' },
  { value: 'education', label: 'Education Technology' },
  { value: 'healthcare', label: 'Healthcare Solutions' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'partnership', label: 'Partnership Opportunity' },
]

const contactCards = [
  { icon: Phone, title: 'Call Us', value: '+91 8390101555', description: 'Mon-Sat, 9:00 AM - 6:00 PM' },
  { icon: Mail, title: 'Email Us', value: 'info@eonixa.in', description: 'We respond within 24 hours' },
  { icon: MapPin, title: 'Visit Us', value: '1107 Sky Iris, Bavdhan', description: 'Pune, Maharashtra - 411001' },
  { icon: Clock, title: 'Working Hours', value: 'Mon - Sat', description: '9:00 AM - 6:00 PM IST' },
]

const whyChooseItems = [
  { icon: Users, text: '50+ Expert Team', desc: 'Skilled professionals at your service' },
  { icon: Briefcase, text: '100+ Projects', desc: 'Successfully delivered across India' },
  { icon: Globe, text: 'Pan-India Presence', desc: 'Serving clients nationwide' },
  { icon: Building2, text: '15+ Years Experience', desc: 'Trusted by Government departments' },
]

export default function Contact() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: 'general',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Create mailto link with form data - sends to both emails
      const subject = encodeURIComponent(`New Inquiry from ${formData.name} - ${departments.find(d => d.value === formData.department)?.label}`)
      const body = encodeURIComponent(
        `New Contact Form Submission\n\n` +
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone || 'Not provided'}\n` +
        `Department: ${departments.find(d => d.value === formData.department)?.label}\n\n` +
        `Message:\n${formData.message}\n\n` +
        `---\nSent from Eonixa Dynamics Website Contact Form`
      )

      // Open default email client - sends to both email addresses
      window.location.href = `mailto:info@eonixa.in,eonixadynamics@gmail.com?subject=${subject}&body=${body}`

      setIsSuccess(true)
      toast.success('Email client opened! Please send the email to complete your inquiry.')

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: 'general',
        message: '',
      })
    } catch (error) {
      console.error('Error submitting form:', error)
      toast.error('Something went wrong. Please try again or email us directly at info@eonixa.in')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      department: 'general',
      message: '',
    })
    setIsSuccess(false)
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | Eonixa Dynamics</title>
        <meta name="description" content="Contact Eonixa Dynamics for Government Technology, Infrastructure & Digital Transformation solutions. Get in touch for consultations, project inquiries, and partnerships." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
              }}
            >
              <MessageSquare className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Get in Touch</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Let's Build the <span className="gradient-text">Future Together</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Ready to transform your organization? Let's discuss how we can help you achieve your digital transformation goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section ref={sectionRef} className="py-8 relative">
        <div className="container-enterprise">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 -mt-20"
          >
            {contactCards.map((card, index) => {
              const Icon = card.icon
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 text-center h-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                      style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-sm text-slate-400 mb-1">{card.title}</p>
                    <p className="text-base font-semibold text-white mb-1">{card.value}</p>
                    <p className="text-xs text-slate-500">{card.description}</p>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <div className="rounded-2xl p-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>

                {isSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">Email Client Opened!</h3>
                    <p className="text-slate-400 mb-6">
                      Your email client has been opened with your inquiry details.<br />
                      Please send the email to complete your inquiry.
                    </p>
                    <p className="text-sm text-slate-500 mb-6">
                      The email will be sent to:<br />
                      <span className="text-primary font-medium">info@eonixa.in</span> & <span className="text-primary font-medium">eonixadynamics@gmail.com</span>
                    </p>
                    <button
                      onClick={handleReset}
                      className="btn-glow px-8 py-3 text-white font-medium rounded-xl transition-all"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                        <input
                          id="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                          style={{
                            background: 'rgba(15, 23, 42, 0.6)',
                            borderColor: 'rgba(99, 102, 241, 0.2)',
                          }}
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                        <input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                          style={{
                            background: 'rgba(15, 23, 42, 0.6)',
                            borderColor: 'rgba(99, 102, 241, 0.2)',
                          }}
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                        <input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                          style={{
                            background: 'rgba(15, 23, 42, 0.6)',
                            borderColor: 'rgba(99, 102, 241, 0.2)',
                          }}
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label htmlFor="department" className="block text-sm font-medium text-slate-300 mb-2">Department *</label>
                        <select
                          id="department"
                          required
                          value={formData.department}
                          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all appearance-none cursor-pointer"
                          style={{
                            background: 'rgba(15, 23, 42, 0.6)',
                            borderColor: 'rgba(99, 102, 241, 0.2)',
                          }}
                        >
                          {departments.map((dept) => (
                            <option key={dept.value} value={dept.value} style={{ background: '#1e293b' }}>
                              {dept.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">Message *</label>
                      <textarea
                        id="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none"
                        style={{
                          background: 'rgba(15, 23, 42, 0.6)',
                          borderColor: 'rgba(99, 102, 241, 0.2)',
                        }}
                        placeholder="Tell us about your project requirements..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-glow w-full flex items-center justify-center gap-2 px-6 py-4 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Opening Email...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          <span>Send Message</span>
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-500 text-center">
                      Your inquiry will be sent to: <span className="text-primary font-medium">info@eonixa.in</span> and <span className="text-primary font-medium">eonixadynamics@gmail.com</span>
                    </p>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Why Choose Us */}
              <div className="rounded-2xl p-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <h3 className="text-xl font-semibold text-white mb-6">Why Choose Eonixa?</h3>
                <div className="space-y-4">
                  {whyChooseItems.map((item, index) => {
                    const Icon = item.icon
                    return (
                      <motion.div
                        key={item.text}
                        initial={{ opacity: 0, x: 20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="flex items-start gap-4"
                      >
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                          }}
                        >
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.text}</p>
                          <p className="text-sm text-slate-400">{item.desc}</p>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}